import React, { useState } from "react";
import PropTypes from "prop-types";
import tw from "twin.macro";
import styled from "styled-components";

import EqualHeightPaddedRow from "components/layout/EqualHeightPaddedRow";

const NavTabs = styled.ul`
  ${tw`flex flex-wrap pl-0 mb-0 list-none`}
`;

const NavItem = styled.li`
  ${tw`mb-0 text-center`}
  flex: 1 1 auto;
`;

const NavLink = styled.a`
  ${tw`flex items-center justify-center cursor-pointer`}
  padding: 0.5rem 1rem;
  border: 0;
  border-bottom: 2px solid
    ${({ isActive }) => (isActive ? "#33aa44" : "#eaedf3")};
  color: ${({ isActive }) =>
    isActive ? "#33aa44" : "rgba(158, 160, 165, 0.7)"};
  background: none;
  text-decoration: none;
  transition: all 0.6s cubic-bezier(0.6, 0.6, 0.7, 1.5);

  &:hover,
  &:focus {
    color: #33aa44;
    text-decoration: none;
  }
`;

const Badge = styled.span`
  ${tw`inline-block whitespace-nowrap text-center align-baseline`}
  font-weight: 700;
  font-size: 75%;
  line-height: 1;
  padding: 5px 10px;
  margin-left: 10px;
  border: 0;
  border-radius: 10px;
  background: ${({ isActive }) => (isActive ? "#33aa44" : "#eaedf3")};
  color: ${({ isActive }) => (isActive ? "white" : "#9ea0a5")};
`;

const TabContent = styled.div``;

const TabPane = styled.div`
  display: ${({ isActive }) => (isActive ? "block" : "none")};
`;

const slugify = (title) =>
  title
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w\-]+/g, "") // eslint-disable-line
    .replace(/\-\-+/g, "-") // eslint-disable-line
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text

const tabs = (props) => React.Children.toArray(props.children);

const renderTabCount = (count, isActive) =>
  count !== undefined ? <Badge isActive={isActive}>{count}</Badge> : "";

const tabHeading = (
  props,
  tabsTitle,
  slug,
  isActive,
  tabInstance,
  index,
  setCurrentTab
) => (
  <NavItem key={slug}>
    <NavLink
      isActive={isActive}
      id={"pills-" + slug + "-tab"}
      data-testid={slug}
      href={"#" + tabsTitle + "-" + slug}
      role="tab"
      onClick={(e) => {
        e.preventDefault();
        setCurrentTab(index);
        if (props && props.actions && props.actions.setTab) {
          props.actions.setTab(slug);
        }
      }}
      aria-controls={tabsTitle + "-" + slug}
      aria-selected={isActive ? "true" : "false"}
    >
      {tabInstance.props.titleContent || tabInstance.props.title}
      {renderTabCount(tabInstance.props.count, isActive)}
    </NavLink>
  </NavItem>
);

const tabBody = (tabsTitle, slug, isActive, tabInstance) => (
  <TabPane
    isActive={isActive}
    id={tabsTitle + "-" + slug}
    role="tabpanel"
    key={slug}
    aria-labelledby={tabsTitle + "-" + slug}
  >
    {tabInstance.props.skipWrapper ? (
      tabInstance
    ) : (
      <EqualHeightPaddedRow>{tabInstance}</EqualHeightPaddedRow>
    )}
  </TabPane>
);

export const Tab = (props) => <React.Fragment>{props.children}</React.Fragment>;

export const Tabs = (props) => {
  const [currentTab, setCurrentTab] = useState(props.initial || 0);

  return (
    <React.Fragment>
      {(!props.bodyOnly && (
        <NavTabs id="pills-tab" role="tablist">
          {tabs(props).map((tab, i) =>
            tabHeading(
              props,
              slugify(props.title),
              slugify(tab.props.title),
              i === currentTab,
              tab,
              i,
              setCurrentTab
            )
          )}
        </NavTabs>
      )) ||
        ""}

      {!props.headerOnly && props.subheading ? props.subheading() : ""}

      {!props.headerOnly && (
        <TabContent id="pills-tabContent">
          {tabs(props).map((tab, i) =>
            tabBody(
              slugify(props.title),
              slugify(tab.props.title),
              i === currentTab,
              tab
            )
          )}
        </TabContent>
      )}
    </React.Fragment>
  );
};

Tabs.propTypes = {
  initial: PropTypes.number,
  titleContent: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.any,
  headerOnly: PropTypes.bool,
  bodyOnly: PropTypes.bool,
  subheading: PropTypes.func
};

Tab.propTypes = {
  title: PropTypes.string,
  count: PropTypes.any,
  children: PropTypes.any
};

export default {
  Tabs: Tabs,
  Tab: Tab
};
